<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { Building2, Cog, FilePlus2, GripVertical, ListChecks, Moon, Plus, Sun, Timer, Trash2, Type } from 'lucide-vue-next'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useI18n } from 'vue-i18n'
import ProductPickerModal from '../components/ProductPickerModal.vue'
import {
  createBomVersion,
  getBomVersion,
  getProduct,
  listDepartmentOptions,
  listEquipmentReferences,
  listOperationReferences,
  updateBomVersion,
} from '../productService'
import {
  bomEditorTotals,
  bomVersionToEditorForm,
  buildBomPayload,
  effectiveMaterialQuantity,
  emptyBomOperation,
  setEditorMaterial,
  standardSocksOperations,
  validateBomEditor,
  type BomEditorErrors,
  type BomEditorForm,
} from '../bomEditor'
import type {
  DepartmentOption,
  EngineeringProduct,
  EquipmentReference,
  OperationReference,
} from '../types'
import { machineTypeLabel, machineTypeOptions } from '../machineTypes'

const route = useRoute()
const router = useRouter()
const client = useApiClient()
const { t } = useI18n()
const productId = computed(() => Number(route.params.id))
const sourceVersionId = computed(() => route.params.bomId ? Number(route.params.bomId) : null)
const isRevision = computed(() => Number.isInteger(sourceVersionId.value) && Number(sourceVersionId.value) > 0)
const product = ref<EngineeringProduct | null>(null)
const sourceVersionNumber = ref<number | null>(null)
const departments = ref<DepartmentOption[]>([])
const operationReferences = ref<OperationReference[]>([])
const equipmentReferences = ref<EquipmentReference[]>([])
const form = reactive<BomEditorForm>({ description: '', operations: [] })
const errors = ref<BomEditorErrors>({})
const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const saveError = ref('')
const materialPickerOpen = ref(false)
const materialOperationIndex = ref<number | null>(null)
const totals = computed(() => bomEditorTotals(form))

/** Machine types available in the equipment catalog (+ values already on the form). */
const availableMachineTypes = computed(() => machineTypeOptions(
  equipmentReferences.value,
  form.operations.map((operation) => operation.equipment_name),
  (key) => t(key),
))
function machineLabel(value: string): string {
  return machineTypeLabel(value, (key) => t(key)) || t('engineering.bom.machineTypeUnset')
}

type OperationTab = 'operation' | 'materials'
/** One card per operation with two tabs: the operation itself and its raw materials. */
const operationTabs = ref<Record<number, OperationTab>>({})
function operationTab(index: number): OperationTab {
  return operationTabs.value[index] ?? 'operation'
}
function setOperationTab(index: number, tab: OperationTab): void {
  operationTabs.value = { ...operationTabs.value, [index]: tab }
}
function tabHasError(index: number, tab: OperationTab): boolean {
  const prefix = `operations.${index}.`
  return Object.keys(errors.value).some((path) => {
    if (!path.startsWith(prefix)) return false
    const isMaterialError = path.includes('.materials.')
    return tab === 'materials' ? isMaterialError : !isMaterialError
  })
}
/** Reveals the tab that holds an error, so validation never fails invisibly. */
function revealErrorTabs(): void {
  const next: Record<number, OperationTab> = { ...operationTabs.value }
  for (const path of Object.keys(errors.value)) {
    const match = /^operations\.(\d+)\./.exec(path)
    if (!match) continue
    next[Number(match[1])] = path.includes('.materials.') ? 'materials' : 'operation'
  }
  operationTabs.value = next
}

/**
 * A BOM may still point at one physical machine; the editor works with the
 * machine type, so an existing reference is translated to its catalog type.
 */
function applyMachineTypes(): void {
  const byId = new Map(equipmentReferences.value.map((item) => [item.id, item]))
  form.operations.forEach((operation) => {
    const equipment = operation.equipment_reference_id
      ? byId.get(operation.equipment_reference_id)
      : undefined
    const type = equipment?.machine_type?.trim()
    if (type) operation.equipment_name = type
    operation.equipment_reference_id = undefined
  })
}

/** "Create BOM" / "Revise BOM v3" — the header shows it as the product subtitle. */
const headerAction = computed(() =>
  isRevision.value
    ? t('engineering.bom.revise', { version: sourceVersionNumber.value })
    : t('engineering.bom.create'),
)

/** "20.00 / 25.00" — day / night piece rate of one operation. */
function rateSummary(operation: BomEditorForm['operations'][number]): string {
  const day = Number(operation.cost_per_produced_unit_day || 0)
  const night = Number(operation.cost_per_produced_unit_night || 0)
  return `${day.toFixed(2)} / ${night.toFixed(2)}`
}

/**
 * Currency appended to the shift-rate fields: the operation reference's own
 * rate currency, else the product's price currency, else the house default.
 */
function operationCurrency(operation: BomEditorForm['operations'][number]): string {
  const reference = operationReferences.value.find(
    (option) => option.id === operation.operation_reference_id,
  )
  const referenceCode = reference?.default_rate_currency_code?.trim()
  if (referenceCode) return referenceCode
  const priceCode = product.value?.prices?.find((price) => price.currency_code)?.currency_code
  return priceCode?.trim() || 'UZS'
}

function replaceForm(next: BomEditorForm): void {
  form.description = next.description
  form.operations.splice(0, form.operations.length, ...next.operations)
}

async function load(): Promise<void> {
  loading.value = true
  loadError.value = ''
  try {
    const requests: [
      Promise<EngineeringProduct>,
      Promise<DepartmentOption[]>,
      Promise<OperationReference[]>,
      Promise<EquipmentReference[]>,
    ] = [
      getProduct(client, productId.value),
      listDepartmentOptions(client),
      listOperationReferences(client),
      listEquipmentReferences(client),
    ]
    const [loadedProduct, loadedDepartments, loadedOperations, loadedEquipment] =
      await Promise.all(requests)
    product.value = loadedProduct
    departments.value = loadedDepartments
    operationReferences.value = loadedOperations
    equipmentReferences.value = loadedEquipment
    if (isRevision.value) {
      const version = await getBomVersion(client, Number(sourceVersionId.value))
      if (version.product_id !== productId.value) throw new Error('The BOM version belongs to another product.')
      sourceVersionNumber.value = version.version_number
      replaceForm(bomVersionToEditorForm(version))
    } else {
      replaceForm({ description: '', operations: [emptyBomOperation(1)] })
    }
    applyMachineTypes()
  } catch (cause) {
    loadError.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

function addOperation(): void {
  const highest = Math.max(0, ...form.operations.map((operation) => operation.operation_sequence))
  form.operations.push(emptyBomOperation(highest + 1))
  // Scroll the freshly created card into view so it is obvious where to type.
  nextTick(() => {
    const cards = document.querySelectorAll<HTMLElement>('.operation-card')
    cards[cards.length - 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

/** Drag & drop sorting of the operation cards (handle-driven). */
const dragIndex = ref<number | null>(null)
const dragOverIndex = ref<number | null>(null)

function onDragStart(index: number, event: DragEvent): void {
  dragIndex.value = index
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', String(index))
  }
}
function onDragOverCard(index: number): void {
  if (dragIndex.value === null) return
  dragOverIndex.value = index
}
function onDragEnd(): void {
  dragIndex.value = null
  dragOverIndex.value = null
}
function onDropCard(index: number): void {
  const from = dragIndex.value
  onDragEnd()
  if (from === null || from === index || from < 0 || from >= form.operations.length) return
  const [operation] = form.operations.splice(from, 1)
  form.operations.splice(index, 0, operation)
  renumberOperations()
}

/** Sequences always follow the visual order of the cards. */
function renumberOperations(): void {
  form.operations.forEach((operation, index) => {
    operation.operation_sequence = index + 1
  })
}

function addSocksRoute(): void {
  if (form.operations.length && !window.confirm(t('engineering.bom.replaceConfirm'))) return
  form.operations.splice(0, form.operations.length, ...standardSocksOperations())
}

function removeOperation(index: number): void {
  if (form.operations[index].materials.length && !window.confirm(t('engineering.bom.removeOperationConfirm'))) return
  form.operations.splice(index, 1)
  renumberOperations()
}

function moveOperation(index: number, direction: -1 | 1): void {
  const target = index + direction
  if (target < 0 || target >= form.operations.length) return
  const [operation] = form.operations.splice(index, 1)
  form.operations.splice(target, 0, operation)
  renumberOperations()
}

function openMaterialPicker(operationIndex: number): void {
  materialOperationIndex.value = operationIndex
  materialPickerOpen.value = true
}

function selectMaterial(material: EngineeringProduct): void {
  const operation = form.operations[materialOperationIndex.value ?? -1]
  if (!operation) return
  if (operation.materials.some((row) => row.material_id === material.id)) {
    errors.value = { materials: `${material.name} ushbu operatsiyaga avval biriktirilgan.` }
    return
  }
  setEditorMaterial(operation, material)
  materialPickerOpen.value = false
  materialOperationIndex.value = null
}

function fieldError(path: string): string {
  return errors.value[path] || ''
}

async function save(): Promise<void> {
  errors.value = validateBomEditor(form, productId.value)
  saveError.value = ''
  if (Object.keys(errors.value).length) {
    revealErrorTabs()
    await nextTick()
    document.querySelector('.field-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    return
  }
  saving.value = true
  try {
    const payload = buildBomPayload(form, productId.value)
    const saved = isRevision.value
      ? await updateBomVersion(client, Number(sourceVersionId.value), payload)
      : await createBomVersion(client, payload)
    await router.push(`/products/${productId.value}?bom=${saved.id}`)
  } catch (cause) {
    saveError.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="page-toolbar">
    <RouterLink class="back-link" :to="`/products/${productId}`">{{ $t('engineering.action.backProduct') }}</RouterLink>
  </div>
  <div v-if="loading" class="page-state">{{ $t('engineering.state.loadingWorkspace') }}</div>
  <div v-else-if="loadError" class="page-state error-state"><p>{{ loadError }}</p><button type="button" @click="load">{{ $t('engineering.action.retry') }}</button></div>
  <template v-else-if="product">
    <header class="editor-header">
      <div>
        <p class="eyebrow">{{ $t('engineering.bom.workspace', { sku: product.sku }) }}</p>
        <h2>{{ product.name }}</h2>
        <p>{{ headerAction }} · {{ $t('engineering.bom.immutableNote') }}</p>
      </div>
      <div class="header-actions">
        <RouterLink class="secondary" :to="`/products/${productId}`">{{ $t('engineering.action.cancel') }}</RouterLink>
        <button class="primary" type="button" :disabled="saving" @click="save">{{ saving ? $t('engineering.action.savingVersion') : $t('engineering.action.saveVersion') }}</button>
      </div>
    </header>

    <section class="summary-strip" aria-label="BOM summary">
      <article><span>{{ $t('engineering.bom.operations') }}</span><strong>{{ totals.operations }}</strong></article>
      <article><span>{{ $t('engineering.bom.bindings') }}</span><strong>{{ totals.materials }}</strong></article>
      <article><span>{{ $t('engineering.bom.standardTime') }}</span><strong>{{ totals.durationMinutes }} min</strong></article>
      <article><span>{{ $t('engineering.bom.costPair') }}</span><strong :title="`${$t('engineering.bom.operationCostDay')} / ${$t('engineering.bom.operationCostNight')}`">{{ totals.operationCost.toFixed(2) }} / {{ totals.operationCostNight.toFixed(2) }}</strong></article>
    </section>

    <section class="version-card">
      <div>
        <h3>{{ $t('engineering.bom.versionControl') }}</h3>
        <p>{{ $t('engineering.bom.versionHelp') }}</p>
      </div>
      <label>{{ $t('engineering.bom.description') }}
        <textarea v-model="form.description" rows="2" maxlength="500" :placeholder="$t('engineering.bom.descriptionPlaceholder')"></textarea>
        <small>{{ form.description.length }} / 500</small>
        <span v-if="fieldError('description')" class="field-error">{{ fieldError('description') }}</span>
      </label>
    </section>

    <div class="routing-heading">
      <div><p class="eyebrow">{{ $t('engineering.bom.route') }}</p><h3>{{ $t('engineering.bom.routeTitle') }}</h3></div>
      <div><button class="secondary" type="button" @click="addSocksRoute">{{ $t('engineering.action.standardRoute') }}</button></div>
    </div>
    <p v-if="fieldError('operations')" class="banner-error field-error">{{ fieldError('operations') }}</p>
    <p v-if="fieldError('materials')" class="banner-error field-error">{{ fieldError('materials') }}</p>
    <p v-if="saveError" class="banner-error" role="alert">{{ saveError }}</p>

    <div class="operation-list">
    <section
      v-for="(operation, operationIndex) in form.operations"
      :key="operationIndex"
      class="operation-card"
      :class="{ 'is-dragging': dragIndex === operationIndex, 'is-drag-over': dragOverIndex === operationIndex }"
      @dragover.prevent="onDragOverCard(operationIndex)"
      @drop.prevent="onDropCard(operationIndex)"
    >
      <header class="operation-header">
        <span
          class="drag-handle"
          draggable="true"
          aria-hidden="true"
          :title="$t('engineering.action.dragHint')"
          @dragstart="onDragStart(operationIndex, $event)"
          @dragend="onDragEnd"
        ><GripVertical :size="16" aria-hidden="true" /></span>
        <span class="sequence">{{ operation.operation_sequence }}</span>
        <div class="operation-title">
          <h3>{{ operation.operation_name || `Operation ${operation.operation_sequence}` }}</h3>
          <p>{{ operation.materials.length }} materials · {{ operation.duration_minutes }} min · {{ rateSummary(operation) }}</p>
        </div>
        <div class="row-actions">
          <button type="button" :disabled="operationIndex === 0" :aria-label="$t('engineering.bom.moveUp')" @click="moveOperation(operationIndex, -1)">↑</button>
          <button type="button" :disabled="operationIndex === form.operations.length - 1" :aria-label="$t('engineering.bom.moveDown')" @click="moveOperation(operationIndex, 1)">↓</button>
          <button class="danger-icon" type="button" @click="removeOperation(operationIndex)"><Trash2 :size="14" aria-hidden="true" />{{ $t('engineering.action.remove') }}</button>
        </div>
      </header>

      <nav class="operation-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          :aria-selected="operationTab(operationIndex) === 'operation'"
          :class="{ 'is-active': operationTab(operationIndex) === 'operation' }"
          @click="setOperationTab(operationIndex, 'operation')"
        >{{ $t('engineering.bom.tabOperation') }}<span v-if="tabHasError(operationIndex, 'operation')" class="tab-alert" :title="$t('engineering.bom.tabHasErrors')">!</span></button>
        <button
          type="button"
          role="tab"
          :aria-selected="operationTab(operationIndex) === 'materials'"
          :class="{ 'is-active': operationTab(operationIndex) === 'materials' }"
          @click="setOperationTab(operationIndex, 'materials')"
        >{{ $t('engineering.bom.tabMaterials') }}<span class="tab-count">{{ operation.materials.length }}</span><span v-if="tabHasError(operationIndex, 'materials')" class="tab-alert" :title="$t('engineering.bom.tabHasErrors')">!</span></button>
      </nav>

      <div v-show="operationTab(operationIndex) === 'operation'" class="operation-fields">
        <div class="operation-fields__row">
          <label v-if="!operation.operation_reference_id" class="field-name">{{ $t('engineering.bom.operationName') }} *<span class="input-group"><span class="input-group__addon input-group__addon--lead" aria-hidden="true"><Type :size="14" /></span><input v-model="operation.operation_name" type="text" :placeholder="$t('engineering.bom.operationName')"></span><span v-if="fieldError(`operations.${operationIndex}.operation_name`)" class="field-error">{{ fieldError(`operations.${operationIndex}.operation_name`) }}</span></label>
          <label class="field-reference">{{ $t('engineering.bom.operationReference') }}<span class="input-group"><span class="input-group__addon input-group__addon--lead" aria-hidden="true"><ListChecks :size="14" /></span><select v-model.number="operation.operation_reference_id"><option :value="undefined">{{ $t('engineering.bom.customOperation') }}</option><option v-for="option in operationReferences" :key="option.id" :value="option.id">{{ option.code }} · {{ option.name }}</option></select></span></label>
          <label class="field-department">{{ $t('engineering.product.department') }} *<span class="input-group"><span class="input-group__addon input-group__addon--lead" aria-hidden="true"><Building2 :size="14" /></span><select v-model.number="operation.department_agent_id"><option :value="undefined">{{ $t('engineering.bom.selectDepartment') }}</option><option v-for="option in departments" :key="option.id" :value="option.id">{{ option.name }}</option></select></span><span v-if="fieldError(`operations.${operationIndex}.department_agent_id`)" class="field-error">{{ fieldError(`operations.${operationIndex}.department_agent_id`) }}</span></label>
          <label class="field-machine">{{ $t('engineering.bom.machineType') }}<span class="input-group"><span class="input-group__addon input-group__addon--lead" aria-hidden="true"><Cog :size="14" /></span><select v-model="operation.equipment_name"><option value="">{{ $t('engineering.bom.machineTypeUnset') }}</option><option v-for="type in availableMachineTypes" :key="type" :value="type">{{ machineLabel(type) }}</option></select></span><small class="field-hint" :title="$t('engineering.bom.machineTypeHelp')">{{ $t('engineering.bom.machineTypeHelp') }}</small></label>
          <label class="field-duration">{{ $t('engineering.bom.duration') }} *<span class="input-group"><span class="input-group__addon input-group__addon--lead" aria-hidden="true"><Timer :size="14" /></span><input v-model.number="operation.duration_minutes" min="0.01" step="0.01" type="number" inputmode="decimal"></span><span v-if="fieldError(`operations.${operationIndex}.duration_minutes`)" class="field-error">{{ fieldError(`operations.${operationIndex}.duration_minutes`) }}</span></label>
          <label class="field-cost">{{ $t('engineering.bom.operationCostDay') }}<span class="input-group"><span class="input-group__addon input-group__addon--lead" aria-hidden="true"><Sun :size="14" /></span><input v-model.number="operation.cost_per_produced_unit_day" min="0" step="0.01" type="number" inputmode="decimal" placeholder="0.00"><span class="input-group__addon input-group__addon--trail">{{ operationCurrency(operation) }}</span></span><span v-if="fieldError(`operations.${operationIndex}.cost_per_produced_unit_day`)" class="field-error">{{ fieldError(`operations.${operationIndex}.cost_per_produced_unit_day`) }}</span></label>
          <label class="field-cost">{{ $t('engineering.bom.operationCostNight') }}<span class="input-group"><span class="input-group__addon input-group__addon--lead" aria-hidden="true"><Moon :size="14" /></span><input v-model.number="operation.cost_per_produced_unit_night" min="0" step="0.01" type="number" inputmode="decimal" placeholder="0.00"><span class="input-group__addon input-group__addon--trail">{{ operationCurrency(operation) }}</span></span><span v-if="fieldError(`operations.${operationIndex}.cost_per_produced_unit_night`)" class="field-error">{{ fieldError(`operations.${operationIndex}.cost_per_produced_unit_night`) }}</span></label>
        </div>
      </div>

      <div v-show="operationTab(operationIndex) === 'materials'" class="operation-materials">
        <div class="materials-heading">
          <div><h4>{{ $t('engineering.bom.materialConsumption') }}</h4><p>{{ $t('engineering.bom.materialHelp') }}</p></div>
          <button class="secondary compact" type="button" @click="openMaterialPicker(operationIndex)"><Plus :size="14" aria-hidden="true" />{{ $t('engineering.action.addMaterial') }}</button>
        </div>
        <div v-if="!operation.materials.length" class="materials-empty">{{ $t('engineering.bom.noOperationMaterials') }}</div>
        <div v-else class="materials-table">
          <table>
            <thead><tr><th>{{ $t('engineering.product.material') }}</th><th>{{ $t('engineering.bom.netQuantity') }}</th><th>{{ $t('engineering.product.unit') }}</th><th>{{ $t('engineering.bom.loss') }}</th><th>{{ $t('engineering.bom.effectiveQuantity') }}</th><th></th></tr></thead>
            <tbody>
              <tr v-for="(material, materialIndex) in operation.materials" :key="material.material_id ?? materialIndex">
                <td><strong>{{ material.material_name }}</strong><small>{{ material.material_sku }} · #{{ material.material_id }}</small><span v-if="fieldError(`operations.${operationIndex}.materials.${materialIndex}.material_id`)" class="field-error">{{ fieldError(`operations.${operationIndex}.materials.${materialIndex}.material_id`) }}</span></td>
                <td><input v-model.number="material.quantity" min="0.000001" step="0.0001" type="number"><span v-if="fieldError(`operations.${operationIndex}.materials.${materialIndex}.quantity`)" class="field-error">{{ fieldError(`operations.${operationIndex}.materials.${materialIndex}.quantity`) }}</span></td>
                <td>{{ material.material_unit }}</td>
                <td><input v-model.number="material.loss_percentage" min="0" max="100" step="0.1" type="number"><span v-if="fieldError(`operations.${operationIndex}.materials.${materialIndex}.loss_percentage`)" class="field-error">{{ fieldError(`operations.${operationIndex}.materials.${materialIndex}.loss_percentage`) }}</span></td>
                <td><strong>{{ effectiveMaterialQuantity(material).toFixed(4) }}</strong> {{ material.material_unit }}</td>
                <td><button class="danger-icon danger-icon--compact" type="button" :aria-label="$t('engineering.action.remove')" :title="$t('engineering.action.remove')" @click="operation.materials.splice(materialIndex, 1)"><Trash2 :size="14" aria-hidden="true" /></button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <span v-if="operationIndex < form.operations.length - 1" class="operation-link" aria-hidden="true"></span>
    </section>
    </div>

    <button class="fab" type="button" :aria-label="$t('engineering.action.addOperation')" :title="$t('engineering.action.addOperation')" @click="addOperation">
      <FilePlus2 :size="18" aria-hidden="true" />{{ $t('engineering.action.addOperation') }}
    </button>
  </template>

  <ProductPickerModal :open="materialPickerOpen" :title="$t('engineering.bom.selectMaterial')" product-type="raw_material" @close="materialPickerOpen = false" @select="selectMaterial" />
</template>

<style scoped>
.back-link{display:inline-block;margin-bottom:16px;color:#285b9a;font-size:13px;font-weight:700}
/* Operation list: cards stack vertically and are drag-sortable by their handle. */
.operation-list{display:flex;flex-direction:column;gap:26px}
.operation-list .operation-card{margin-bottom:0}
.operation-card{position:relative;scroll-margin-top:90px}
.operation-card.is-dragging{opacity:.5}
.operation-card.is-drag-over{outline:2px dashed #17324D;outline-offset:3px}
/* Chain connector: a navy rail with a white line and a white downward arrowhead. */
.operation-link{position:absolute;left:50%;bottom:-26px;width:20px;height:26px;transform:translateX(-50%);border-radius:0 0 10px 10px;background:transparent;pointer-events:none}
.operation-link::before{content:'';position:absolute;left:50%;top:0;width:3px;height:15px;transform:translateX(-50%);background:#fff}
.operation-link::after{content:'';position:absolute;left:50%;bottom:3px;width:0;height:0;transform:translateX(-50%);border-left:7px solid transparent;border-right:7px solid transparent;border-top:9px solid #fff}
.drag-handle{display:inline-grid;flex:0 0 auto;width:26px;height:34px;color:#7d8b86;cursor:grab;place-items:center}
.drag-handle:hover{color:#17324D}
.drag-handle:active{cursor:grabbing}
.operation-header{align-items:center;padding-bottom:10px;border-bottom:1px solid #e1e7e5}
.operation-card>.operation-header{padding-bottom:10px;gap:10px}
.operation-title{flex:1;min-width:0}
.operation-title h3{overflow:hidden;margin:0;font-size:15px;text-overflow:ellipsis;white-space:nowrap}
/* Folder-style tabs: the active tab joins the white body below it. */
.operation-tabs{display:flex;align-items:flex-end;gap:4px;margin:12px 0 0;padding-left:2px;border-bottom:1px solid #d7dfdc}
.operation-tabs button{position:relative;display:inline-flex;align-items:center;gap:6px;margin-bottom:-1px;padding:7px 16px;border:1px solid #d7dfdc;border-bottom-color:transparent;border-radius:8px 8px 0 0;color:#53625d;background:#eef2f1;font:inherit;font-size:12px;font-weight:700;cursor:pointer}
.operation-tabs button:hover{background:#e3eae7;color:#17324d}
.operation-tabs button.is-active{border-color:#d7dfdc;border-bottom-color:#fff;color:#17324d;background:#fff;box-shadow:0 1px 0 #fff}
.tab-count{min-width:18px;padding:1px 5px;border-radius:9px;color:#53625d;background:#eef2f1;font-size:10px;font-weight:800;text-align:center}
.operation-tabs button.is-active .tab-count{background:#e8f4ef;color:#0b6b4f}
.tab-alert{display:inline-grid;width:15px;height:15px;border-radius:50%;color:#fff;background:#c0392b;font-size:10px;font-weight:800;line-height:1;place-items:center}
/* Solid red buttons carry no border (house style) and always show an icon. */
.danger-icon,.row-actions .danger-icon,.materials-table .danger-icon{display:inline-flex;align-items:center;justify-content:center;gap:6px;padding:6px 10px;border:0;color:#fff;background:#c0392b;font:inherit;font-size:12px;font-weight:700;cursor:pointer}
.danger-icon:hover,.row-actions .danger-icon:hover,.materials-table .danger-icon:hover{color:#fff;background:#a5301f}
.danger-icon--compact,.row-actions .danger-icon--compact,.materials-table .danger-icon--compact{padding:6px 8px}
.danger-icon:focus-visible{outline:3px solid rgba(192,57,43,.35);outline-offset:2px}
.operation-card .operation-fields{gap:10px;margin-top:12px}
.operation-card input,.operation-card select{padding:7px 8px;font-size:12px}
.field-hint{color:#66726e;font-size:10px;font-weight:500}
.operation-materials{padding-top:6px}
.compact,.secondary.compact{display:inline-flex;align-items:center;gap:5px;padding:5px 9px;font-size:12px}
.fab{position:fixed;z-index:40;right:24px;bottom:24px;display:inline-flex;align-items:center;gap:8px;padding:13px 18px;border:0;border-radius:999px;color:#fff;background:#07996f;font:inherit;font-size:13px;font-weight:800;box-shadow:0 10px 24px rgba(7,153,111,.34);cursor:pointer}
.fab:hover{background:#068a63}
.fab:focus-visible{outline:3px solid rgba(7,153,111,.4);outline-offset:2px}
@media(max-width:620px){
.operation-tabs button{flex:1;justify-content:center;padding:8px 6px}
.fab{right:16px;bottom:16px;padding:12px 16px}
}.editor-header,.routing-heading,.operation-card>header,.materials-heading{display:flex;align-items:flex-start;justify-content:space-between;gap:18px}.eyebrow{margin:0 0 5px;color:#66726e;font-size:11px;font-weight:800;text-transform:uppercase}.editor-header h2{margin:0;font-size:26px}.editor-header p,.routing-heading p,.version-card p,.operation-card header p,.materials-heading p{margin:5px 0 0;color:#66726e;font-size:13px}.header-actions,.routing-heading>div:last-child,.row-actions{display:flex;gap:8px}.primary,.secondary{display:inline-flex;align-items:center;justify-content:center;padding:9px 13px;font:inherit;font-size:13px;font-weight:700;text-decoration:none;cursor:pointer}.primary{color:#fff;background:#07996f;border:0}.secondary{border:1px solid #b9c5c1;color:#285b9a;background:#fff}.primary:disabled{opacity:.6;cursor:wait}.summary-strip{display:grid;grid-template-columns:repeat(4,1fr);gap:9px;margin:18px 0}.summary-strip article{display:flex;align-items:center;justify-content:space-between;padding:13px;border:1px solid #d7dfdc;background:#fff}.summary-strip span{color:#66726e;font-size:12px}.summary-strip strong{font-size:18px}.version-card,.operation-card{margin-bottom:14px;padding:20px;border:1px solid #d7dfdc;background:#fff}.version-card{display:grid;grid-template-columns:minmax(220px,.45fr) minmax(0,1fr);gap:22px}.version-card h3,.routing-heading h3,.operation-card h3,.materials-heading h4{margin:0}.version-card label,.operation-fields label{display:grid;align-content:start;gap:5px;color:#53625d;font-size:12px;font-weight:700}.version-card textarea,input,select{width:100%;box-sizing:border-box;padding:9px 10px;border:1px solid #b9c5c1;background:#fff;font:inherit}.version-card small{justify-self:end;color:#66726e;font-weight:400}.routing-heading{align-items:end;margin:22px 0 10px}.operation-card>header{align-items:center;padding-bottom:14px;border-bottom:1px solid #e1e7e5}.sequence{display:grid;flex:0 0 34px;width:34px;height:34px;color:#fff;background:#3568a8;font-weight:800;place-items:center}.operation-card>header>div:nth-child(2){flex:1}.row-actions button,.danger-link{padding:6px 9px;border:1px solid #ccd5d2;color:#285b9a;background:#fff;font:inherit;font-size:12px;font-weight:700;cursor:pointer}.row-actions button:disabled{opacity:.35;cursor:default}.danger-link{border-color:#e1b1aa;color:#852d22}.operation-fields{display:flex;flex-direction:column;gap:12px;margin-top:16px}.field-row{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;align-items:start}.field-row>label{min-width:0}.field-row--pay{padding-top:12px;border-top:1px dashed #e1e7e5}.field-error{color:#a03528;font-size:11px;font-weight:600}.banner-error{padding:10px 12px;border:1px solid #e1b1aa;color:#852d22;background:#fff5f3}.materials-heading{align-items:end;margin-top:20px}.materials-empty{margin-top:12px;padding:18px;color:#66726e;background:#f3f6f5;text-align:center}.materials-table{margin-top:12px;overflow-x:auto}.materials-table table{width:100%;border-collapse:collapse}.materials-table th,.materials-table td{padding:9px;border:1px solid #e1e7e5;text-align:left;vertical-align:top}.materials-table th{color:#53625d;background:#f3f6f5;font-size:11px;text-transform:uppercase}.materials-table td:first-child{min-width:190px}.materials-table td input{min-width:110px}.materials-table td small{display:block;margin-top:3px;color:#66726e}.page-state{display:grid;min-height:320px;place-items:center}.error-state{color:#852d22}@media(max-width:900px){.summary-strip{grid-template-columns:repeat(2,1fr)}.field-row{grid-template-columns:repeat(2,minmax(0,1fr))}.version-card{grid-template-columns:1fr}}@media(max-width:620px){.editor-header,.routing-heading,.operation-card>header,.materials-heading{align-items:stretch;flex-direction:column}.summary-strip,.field-row{grid-template-columns:1fr}.header-actions,.routing-heading>div:last-child{flex-wrap:wrap}}
/* Operation tab: every input sits on one compact row (name → reference → department →
   machine type → duration → day rate → night rate). The sequence is drag-sorted, so it
   is shown read-only in the card header instead of being edited here. */
.operation-card{padding:16px}
.operation-card .operation-fields{gap:8px;margin-top:10px}
.operation-card .operation-fields label{min-width:0}
.operation-card .operation-fields__row{display:flex;flex-wrap:wrap;align-items:flex-start;gap:8px}
.operation-card .operation-fields__row>label{flex:1 1 100px;min-width:0}
.operation-card .operation-fields__row>.field-duration{flex:0 0 104px}
/* Field addons: a Lucide icon on the left, the currency code on the right. */
.operation-card .input-group{display:flex;align-items:stretch;min-width:0}
.operation-card .input-group>input,
.operation-card .input-group>select{flex:1 1 auto;min-width:0}
.operation-card .input-group>input:only-child,
.operation-card .input-group>select:only-child{border-radius:var(--ui-control-radius,6px) !important}
.operation-card .input-group>input:first-child:not(:only-child),
.operation-card .input-group>select:first-child:not(:only-child){border-radius:var(--ui-control-radius,6px) 0 0 var(--ui-control-radius,6px) !important}
.operation-card .input-group>input:last-child:not(:only-child),
.operation-card .input-group>select:last-child:not(:only-child){border-radius:0 var(--ui-control-radius,6px) var(--ui-control-radius,6px) 0 !important}
.operation-card .input-group>input:not(:first-child):not(:last-child),
.operation-card .input-group>select:not(:first-child):not(:last-child){border-radius:0 !important}
.operation-card .input-group__addon{display:inline-flex;align-items:center;justify-content:center;flex:0 0 auto;padding:0 7px;border:1px solid #b9c5c1;color:#53625d;background:#f3f6f5;font-size:11px;font-weight:700;letter-spacing:.02em;white-space:nowrap}
.operation-card .input-group__addon svg{display:block;color:#7d8b86}
.operation-card .input-group__addon--lead{border-right:0;border-radius:var(--ui-control-radius,6px) 0 0 var(--ui-control-radius,6px)}
.operation-card .input-group__addon--trail{border-left:0;border-radius:0 var(--ui-control-radius,6px) var(--ui-control-radius,6px) 0}
/* Hints/errors stay on one line so the compact row keeps a single height. */
.operation-card .operation-fields__row .field-hint{display:block;max-width:100%;overflow:hidden;font-size:10px;text-overflow:ellipsis;white-space:nowrap}
.operation-card .operation-fields .field-error{font-size:11px;line-height:1.25}
@media(max-width:620px){
.operation-card .operation-fields__row>label,
.operation-card .operation-fields__row>.field-duration{flex:1 1 100%}
}
</style>
